import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaChevronDown,
  FaRegClock,
  FaUser,
  FaPlus,
  FaTimes,
  FaEye,
  FaWhatsapp,
} from "react-icons/fa";
import { toDate } from "helpers/dateHelper";
import { getFullName } from "helpers/utils";
import { useGetMembers } from "hooks/useGetMembers";
import { useGetTickets } from "hooks/useGetTickets";
import { useGetTicketMessages } from "hooks/useGetTicketMessages";
import { useGetCategories } from "hooks/useGetCategories";
import { useGetBranches } from "hooks/useGetBranches";
import { Ticket, TicketStatus, TicketType } from "interfaces/entities/ticket.model";
import {
  Avatar,
  DesignPriority,
  Popover,
  PopoverItem,
  PriorityPill,
  StatusPill,
  ALL_DESIGN_PRIORITIES,
} from "./primitives";
import {
  computeSlaState,
  formatDuration,
  slaFirstResponseSeconds,
  slaResolutionSeconds,
  SlaState,
} from "./mappings";
import { PlatformIcon } from "components/PlatformIcon/PlatformIcon";

interface SidebarColumnProps {
  ticket: Ticket;
  status: string;
  onStatusChange: (s: TicketStatus) => void;
  priority: DesignPriority;
  onPriorityChange: (p: DesignPriority) => void;
  assigneeID: string | null;
  onAssigneeChange: (id: string | null) => void;
  type: string;
  onTypeChange: (t: string) => void;
  categoryID: string;
  onCategoryChange: (id: string) => void;
  branchID: string;
  onBranchChange: (id: string) => void;
  tags: string[];
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
}

export const SidebarColumn = ({
  ticket,
  status,
  onStatusChange,
  priority,
  onPriorityChange,
  assigneeID,
  onAssigneeChange,
  type,
  onTypeChange,
  categoryID,
  onCategoryChange,
  branchID,
  onBranchChange,
  tags,
  onAddTag,
  onRemoveTag,
}: SidebarColumnProps) => {
  const { t } = useTranslation();
  const orgID = useMemo(() => localStorage.getItem("orgID") || "", []);
  const { data: membersResponse } = useGetMembers(orgID);
  const members: any[] = (membersResponse as any)?.data || [];
  const { data: categoriesResponse } = useGetCategories({ type: "TICKET", orgID });
  const categories: any[] = (categoriesResponse as any)?.data || [];
  const { data: branchesResponse } = useGetBranches({ orgID });
  const branches: any[] = (branchesResponse as any)?.data || [];

  // Other tickets from the same customer — used in the "Связанные обращения"
  // section. We exclude the current ticket and cap the visible list at 10
  // so the sidebar stays compact. Skipped entirely when there is no
  // customer attached.
  const { data: relatedResponse } = useGetTickets(
    ticket.customerID ? { customerID: ticket.customerID, limit: 20 } : { limit: 0 },
  );
  const relatedTickets: Ticket[] = useMemo(() => {
    const rows: Ticket[] = ((relatedResponse as any)?.rows || []) as Ticket[];
    if (!ticket.customerID) return [];
    return rows.filter((r) => r.id !== ticket.id).slice(0, 10);
  }, [relatedResponse, ticket.customerID, ticket.id]);

  const [statusOpen, setStatusOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  const [tagDraft, setTagDraft] = useState("");

  // === SLA ===
  // Цели берём по приоритету (как у бэка: HIGH=8ч, MEDIUM=24ч, LOW=72ч).
  // Первый ответ — момент самого раннего публичного сообщения сотрудника
  // (isInternal=false, memberID не null). Решение — resolvedAt; финальный
  // отсчёт замораживается, если тикет закрыт/решён.
  const { data: messages } = useGetTicketMessages(ticket.id);
  const firstStaffReplyAt = useMemo(() => {
    const rows: any[] = Array.isArray(messages) ? messages : [];
    const replies = rows
      .filter((m) => m?.memberID && !m?.isInternal && m?.createdAt)
      .map((m) => new Date(m.createdAt).getTime())
      .filter((t) => Number.isFinite(t));
    if (!replies.length) return null;
    return Math.min(...replies);
  }, [messages]);

  const resolutionTargetSeconds = slaResolutionSeconds(priority);
  const firstResponseTargetSeconds = slaFirstResponseSeconds(priority);
  const createdAtMs = ticket.createdAt ? new Date(ticket.createdAt).getTime() : Date.now();
  const resolvedAtMs = ticket.resolvedAt
    ? new Date(ticket.resolvedAt).getTime()
    : ticket.closedAt
      ? new Date(ticket.closedAt).getTime()
      : null;

  // Тикает раз в секунду, чтобы счётчик «осталось / просрочено» жил.
  // Замораживаем, когда тикет уже решён/закрыт — отсчёт там бессмысленный.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (resolvedAtMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [resolvedAtMs]);

  // Первый ответ: либо «Выполнен за …» (если есть staff-сообщение), либо
  // обратный отсчёт до целевого времени первого ответа от createdAt.
  const firstResponse: { state: SlaState; label: string } = useMemo(() => {
    if (firstStaffReplyAt) {
      const elapsed = Math.max(0, Math.floor((firstStaffReplyAt - createdAtMs) / 1000));
      const breached = elapsed > firstResponseTargetSeconds;
      return {
        state: breached ? "breached" : "met",
        label: breached
          ? `С опозданием · ${formatDuration(elapsed)}`
          : `Выполнен за ${formatDuration(elapsed)}`,
      };
    }
    const deadline = createdAtMs + firstResponseTargetSeconds * 1000;
    const secondsLeft = Math.floor((deadline - now) / 1000);
    return computeSlaState(secondsLeft, firstResponseTargetSeconds);
  }, [firstStaffReplyAt, createdAtMs, firstResponseTargetSeconds, now]);

  // Время до решения: пока тикет активен — countdown до createdAt+target,
  // после resolve/close — финальное значение «Решено за …» либо «С опозданием».
  const resolutionDeadlineMs = createdAtMs + resolutionTargetSeconds * 1000;
  const resolution: { state: SlaState; label: string; secondsLeft: number } = useMemo(() => {
    if (resolvedAtMs) {
      const elapsed = Math.max(0, Math.floor((resolvedAtMs - createdAtMs) / 1000));
      const breached = elapsed > resolutionTargetSeconds;
      return {
        state: breached ? "breached" : "met",
        label: breached
          ? `С опозданием · ${formatDuration(elapsed)}`
          : `Решено за ${formatDuration(elapsed)}`,
        secondsLeft: Math.max(0, resolutionTargetSeconds - elapsed),
      };
    }
    const secondsLeft = Math.floor((resolutionDeadlineMs - now) / 1000);
    return { ...computeSlaState(secondsLeft, resolutionTargetSeconds), secondsLeft };
  }, [resolvedAtMs, createdAtMs, resolutionDeadlineMs, resolutionTargetSeconds, now]);

  const resolutionPct = Math.max(
    0,
    Math.min(100, (Math.max(0, resolution.secondsLeft) / resolutionTargetSeconds) * 100),
  );

  const customer = ticket.customer;
  const customerName = customer ? getFullName(customer) : "—";

  const assignee = members.find((m) => m.id === assigneeID);

  const commitTag = () => {
    const v = tagDraft.trim().toLowerCase().replace(/\s+/g, "-");
    if (v && !tags.includes(v)) onAddTag(v);
    setTagDraft("");
    setAddingTag(false);
  };

  const tagInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (addingTag) tagInputRef.current?.focus();
  }, [addingTag]);

  return (
    <aside className="tv2-sidebar" aria-label="Свойства обращения">
      {/* Customer */}
      <div className="tv2-sidebar-section">
        <div className="tv2-sidebar-section-header">
          <span className="tv2-sidebar-section-title">Абонент</span>
        </div>
        <div className="tv2-customer-card">
          <div className="tv2-customer-top">
            <Avatar name={customerName} size="xl" seed={String(customer?.id || customerName)} />
            <div>
              {customer?.id ? (
                <Link to={`/admin/customers/view/${customer.id}`} className="tv2-customer-name">
                  {customerName}
                </Link>
              ) : (
                <div className="tv2-customer-name">{customerName}</div>
              )}
            </div>
          </div>
          <div className="tv2-customer-fields">
            <Field label="Телефон" value={customer?.phone || customer?.user?.phone || "—"} />
            <Field label="Канал" value={ticket?.source || "—"} />
          </div>
        </div>
      </div>

      {/* Properties */}
      <div className="tv2-sidebar-section">
        <div className="tv2-sidebar-section-header">
          <span className="tv2-sidebar-section-title">Свойства</span>
        </div>

        <div className="tv2-prop-row">
          <span className="tv2-prop-label">Статус</span>
          <button
            type="button"
            className="tv2-prop-value-btn"
            onClick={() => setStatusOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={statusOpen}
          >
            <StatusPill status={status} />
            <span className="tv2-spacer" />
            <FaChevronDown size={11} style={{ color: "var(--tv2-text-3)" }} />
          </button>
          <Popover open={statusOpen} onClose={() => setStatusOpen(false)}>
            {Object.values(TicketStatus).map((s) => (
              <PopoverItem
                key={s}
                selected={s === status}
                onClick={() => {
                  onStatusChange(s);
                  setStatusOpen(false);
                }}
              >
                <StatusPill status={s} />
              </PopoverItem>
            ))}
          </Popover>
        </div>

        <div className="tv2-prop-row">
          <span className="tv2-prop-label">Приоритет</span>
          <button
            type="button"
            className="tv2-prop-value-btn"
            onClick={() => setPriorityOpen((v) => !v)}
          >
            <PriorityPill priority={priority} />
            <span className="tv2-spacer" />
            <FaChevronDown size={11} style={{ color: "var(--tv2-text-3)" }} />
          </button>
          <Popover open={priorityOpen} onClose={() => setPriorityOpen(false)}>
            {ALL_DESIGN_PRIORITIES.map((p) => (
              <PopoverItem
                key={p}
                selected={p === priority}
                onClick={() => {
                  onPriorityChange(p);
                  setPriorityOpen(false);
                }}
              >
                <PriorityPill priority={p} />
              </PopoverItem>
            ))}
          </Popover>
        </div>

        <div className="tv2-prop-row">
          <span className="tv2-prop-label">Ответственный</span>
          <button
            type="button"
            className="tv2-prop-value-btn"
            onClick={() => setAssigneeOpen((v) => !v)}
          >
            {assignee ? (
              <>
                <Avatar name={assignee.name || "?"} size="sm" />
                <span style={{ fontSize: 13 }}>{assignee.name || assignee.userID}</span>
              </>
            ) : (
              <span style={{ color: "var(--tv2-text-3)", fontSize: 13 }}>Не назначен</span>
            )}
            <span className="tv2-spacer" />
            <FaChevronDown size={11} style={{ color: "var(--tv2-text-3)" }} />
          </button>
          <Popover open={assigneeOpen} onClose={() => setAssigneeOpen(false)}>
            <PopoverItem
              selected={!assigneeID}
              onClick={() => {
                onAssigneeChange(null);
                setAssigneeOpen(false);
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: "var(--tv2-surface-2)",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--tv2-text-3)",
                }}
              >
                <FaUser size={10} />
              </span>
              <span>Не назначен</span>
            </PopoverItem>
            {members.length > 0 && <div className="tv2-popover-divider" />}
            {members.map((m) => (
              <PopoverItem
                key={m.id}
                selected={m.id === assigneeID}
                onClick={() => {
                  onAssigneeChange(m.id);
                  setAssigneeOpen(false);
                }}
              >
                <Avatar name={m.name || "?"} size="sm" />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.3,
                  }}
                >
                  <span>{m.name || m.userID}</span>
                </div>
              </PopoverItem>
            ))}
          </Popover>
        </div>

        {/* Type */}
        <div className="tv2-prop-row">
          <span className="tv2-prop-label">Тип</span>
          <button
            type="button"
            className="tv2-prop-value-btn"
            onClick={() => setTypeOpen((v) => !v)}
          >
            <span style={{ fontSize: 13 }}>
              {type ? (
                t(`ticket.type.${type}`, { defaultValue: type })
              ) : (
                <span style={{ color: "var(--tv2-text-3)" }}>— не выбран —</span>
              )}
            </span>
            <span className="tv2-spacer" />
            <FaChevronDown size={11} style={{ color: "var(--tv2-text-3)" }} />
          </button>
          <Popover open={typeOpen} onClose={() => setTypeOpen(false)}>
            {Object.values(TicketType).map((tp: TicketType) => (
              <PopoverItem
                key={tp}
                selected={tp === type}
                onClick={() => {
                  onTypeChange(tp);
                  setTypeOpen(false);
                }}
              >
                {t(`ticket.type.${tp}`, { defaultValue: tp })}
              </PopoverItem>
            ))}
          </Popover>
        </div>

        {/* Category (тематика) */}
        <div className="tv2-prop-row">
          <span className="tv2-prop-label">Тематика</span>
          <button
            type="button"
            className="tv2-prop-value-btn"
            onClick={() => setCategoryOpen((v) => !v)}
          >
            <span style={{ fontSize: 13 }}>
              {categoryID ? (
                categories.find((c) => c.id === categoryID)?.name || "—"
              ) : (
                <span style={{ color: "var(--tv2-text-3)" }}>— не выбрана —</span>
              )}
            </span>
            <span className="tv2-spacer" />
            <FaChevronDown size={11} style={{ color: "var(--tv2-text-3)" }} />
          </button>
          <Popover open={categoryOpen} onClose={() => setCategoryOpen(false)}>
            <PopoverItem
              selected={!categoryID}
              onClick={() => {
                onCategoryChange("");
                setCategoryOpen(false);
              }}
            >
              <span style={{ color: "var(--tv2-text-3)" }}>— не выбрана —</span>
            </PopoverItem>
            {categories.length > 0 && <div className="tv2-popover-divider" />}
            {categories.map((c) => (
              <PopoverItem
                key={c.id}
                selected={c.id === categoryID}
                onClick={() => {
                  onCategoryChange(c.id);
                  setCategoryOpen(false);
                }}
              >
                {c.name}
              </PopoverItem>
            ))}
          </Popover>
        </div>

        {/* Branch (филиал) */}
        <div className="tv2-prop-row">
          <span className="tv2-prop-label">Филиал</span>
          <button
            type="button"
            className="tv2-prop-value-btn"
            onClick={() => setBranchOpen((v) => !v)}
          >
            <span style={{ fontSize: 13 }}>
              {branchID ? (
                branches.find((b) => b.id === branchID)?.name || "—"
              ) : (
                <span style={{ color: "var(--tv2-text-3)" }}>— не выбран —</span>
              )}
            </span>
            <span className="tv2-spacer" />
            <FaChevronDown size={11} style={{ color: "var(--tv2-text-3)" }} />
          </button>
          <Popover open={branchOpen} onClose={() => setBranchOpen(false)}>
            <PopoverItem
              selected={!branchID}
              onClick={() => {
                onBranchChange("");
                setBranchOpen(false);
              }}
            >
              <span style={{ color: "var(--tv2-text-3)" }}>— не выбран —</span>
            </PopoverItem>
            {branches.length > 0 && <div className="tv2-popover-divider" />}
            {branches.map((b) => (
              <PopoverItem
                key={b.id}
                selected={b.id === branchID}
                onClick={() => {
                  onBranchChange(b.id);
                  setBranchOpen(false);
                }}
              >
                {b.name}
              </PopoverItem>
            ))}
          </Popover>
        </div>

        <div className="tv2-prop-row" style={{ alignItems: "flex-start", paddingTop: 10 }}>
          <span className="tv2-prop-label" style={{ paddingTop: 4 }}>
            Теги
          </span>
          <div className="tv2-tags">
            {tags.map((t) => (
              <span key={t} className="tv2-tag">
                {t}
                <button
                  type="button"
                  className="tv2-tag-remove"
                  onClick={() => onRemoveTag(t)}
                  aria-label={`Удалить тег ${t}`}
                >
                  <FaTimes size={9} />
                </button>
              </span>
            ))}
            {addingTag ? (
              <input
                ref={tagInputRef}
                className="tv2-tag-input"
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onBlur={commitTag}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitTag();
                  if (e.key === "Escape") {
                    setTagDraft("");
                    setAddingTag(false);
                  }
                }}
                placeholder="новый тег"
              />
            ) : (
              <button type="button" className="tv2-add-tag" onClick={() => setAddingTag(true)}>
                <FaPlus size={9} /> Добавить
              </button>
            )}
          </div>
        </div>
      </div>

      {/* SLA */}
      <div className="tv2-sidebar-section">
        <div className="tv2-sidebar-section-header">
          <span className="tv2-sidebar-section-title">SLA</span>
          <span className="tv2-sidebar-section-action">
            <FaRegClock size={11} />
          </span>
        </div>
        <div className="tv2-sla">
          <div className="tv2-sla-row">
            <span className="tv2-sla-label">Первый ответ</span>
            <span className={`tv2-sla-time ${firstResponse.state}`}>{firstResponse.label}</span>
          </div>
          <div>
            <div className="tv2-sla-row" style={{ marginBottom: 6 }}>
              <span className="tv2-sla-label">Время до решения</span>
              <span className={`tv2-sla-time ${resolution.state}`}>{resolution.label}</span>
            </div>
            <div className="tv2-sla-bar">
              <div
                className={`tv2-sla-bar-fill ${resolution.state}`}
                style={{ width: `${resolutionPct}%` }}
              />
            </div>
          </div>
          <div className="tv2-sla-row" style={{ color: "var(--tv2-text-3)", fontSize: 11 }}>
            <span>Цель</span>
            <span>{Math.round(resolutionTargetSeconds / 3600)}ч по приоритету</span>
          </div>
        </div>
      </div>

      {/* Related tickets — other tickets from the same customer. ID +
          subject + status pill, each row links to the v2 page so the
          operator can hop between conversations. */}
      <div className="tv2-sidebar-section">
        <div className="tv2-sidebar-section-header">
          <span className="tv2-sidebar-section-title">Связанные обращения</span>
          {relatedTickets.length > 0 && (
            <span className="tv2-sidebar-section-action">{relatedTickets.length}</span>
          )}
        </div>
        {!ticket.customerID ? (
          <div className="tv2-empty-hint">Клиент не указан.</div>
        ) : relatedTickets.length === 0 ? (
          <div className="tv2-empty-hint">Других обращений у клиента нет.</div>
        ) : (
          <div className="tv2-related">
            {relatedTickets.map((r) => (
              <Link
                key={r.id}
                to={`/admin/tickets/${r.id}/v2`}
                className="tv2-related-item"
                title={r.subject || "Без темы"}
              >
                <span className="tv2-related-id">#{r.id}</span>
                <span className="tv2-related-title">{r.subject || "Без темы"}</span>
                <StatusPill status={r.status} />
              </Link>
            ))}
          </div>
        )}
        {ticket.createdAt && (
          <div className="tv2-mock-note" style={{ paddingTop: 8 }}>
            Создан {toDate(ticket.createdAt)}
          </div>
        )}
      </div>
    </aside>
  );
};

interface FieldProps {
  label: string;
  value: string;
  mono?: boolean;
}
const Field = ({ label, value, mono }: FieldProps) => (
  <div className="tv2-customer-field">
    <span className="tv2-customer-field-label">{label}</span>
    <span className={`tv2-customer-field-value${mono ? " mono" : ""}`} title={value}>
      {["WHATSAPP"].includes(value) ? <PlatformIcon platform={value} /> : value}
    </span>
  </div>
);
